import { computed } from 'vue';
import { getRecordType, getInfoBucket } from '@/utils/formatters.js';

// ─── Follow-up state engine ────────────────────────────────────────────────
// Single source of truth for "what work is waiting inside this informational
// record". Extracted from RecordsView.vue (2026-08-08) so the Records page's
// row pills and AI Reports' Action Center read the SAME state machine rather
// than two drifting copies of it — this logic carries several hard-won bug
// fixes (documented inline below) that a second implementation would lose.
//
// This module returns STRUCTURED state ({ kind, count, ... }). Presentation
// lives in followUpPill() at the bottom, so a consumer that wants to rank,
// group or filter never has to parse a label string to find out what a row is.
//
// Surfaces informational records (ARs / Arrests / UOF / Complaints lists) that
// have work waiting in them, so the Records page shows WHICH rows to click
// rather than requiring each one to be opened to find out.
//
// Annotations live at the END of a case line, after the tab- or pipe-separated
// case data — e.g. "25-06987\tPosyton III, Ronald\t02/16/2025    YES". They are
// not leading tokens, so these patterns scan the whole line.
//
// Validated against the current export: of 492 informational records in hand and
// not marked DONE, 84 have yes/maybe cases never requested (214 cases total),
// 71 have a list that was never reviewed, and 67 are part-reviewed. Those counts
// are what make this worth a siren rather than noise.

const FU_HANDLED = /\b(requested|got|kill(ed)?|denied|juvi|juvenile|open)\b/i;
const FU_YESMAYBE = /\b(yes!?|maybe)\b/i;
const FU_NEGATIVE = /\b(no|nope|nah|meh|not)\b/i;

// ── "no" that is DATA, not a rating ────────────────────────────────────────
// FU_NEGATIVE scans the whole line (annotations sit at the end, not the
// start — see the header comment), so any bare "no" inside the case data
// scored the line as a decided No. Measured on the 2026-08-09 export: 385 of
// 769 negative lines were false positives, and the damage is not just a
// miscount — one fake negative makes `negative === 0` false, so the
// not_reviewed branch never runs and a batch nobody has touched reports as
// part-reviewed ("N left"). Kankakee 2024 ARs 1 landed on "4 left" the day it
// arrived, with 6 unrated cases and nothing rated at all.
//
// Three shapes, all of them structural rather than a word blacklist:
//
//   Field LABEL — "NAME | Case No. 2025-00006392 | Arrest Date ...", the line
//     format rev-AR writes. 383 of the 385. FU_HEADER already knows this
//     shape but only ever used it to DISCARD a bare header row, so a real
//     data row carrying the label inline kept the false positive.
//   Slash run — charge-code text: "Agg UUW/Loaded/No FCCA/FOID". A rating is
//     appended after the case data and is never punctuation-welded to it.
//   Parenthetical — what the agency typed in a column: "N/A (No Arrest)".
//
// Deliberately applied to the NEGATIVE test only. It is the one keyword set
// that collides with ordinary record text, and yes/maybe/handled are checked
// against the raw line first, so a rating like "(no - juvi)" still reads as
// handled. Hyphen-prefixed ratings ("... CINDY J -meh", "SP24-18323 -no",
// 6 in that export) are real and must survive — which is why this strips
// known data shapes rather than requiring the keyword to start a token.
const FU_FIELD_LABEL = /\b(?:case|report|incident|file|booking|citation)\s*(?:no\b\.?|number\b|#)/gi;
const FU_SLASH_RUN = /\/(?:no|not|nah|meh|nope)\b|\b(?:no|not|nah|meh|nope)\//gi;
const FU_PARENTHETICAL = /\([^)]*\)/g;

function ratingText(line) {
  return String(line || '')
    .replace(FU_FIELD_LABEL, ' ')
    .replace(FU_SLASH_RUN, ' ')
    .replace(FU_PARENTHETICAL, ' ');
}

function isNegativeLine(line) {
  return FU_NEGATIVE.test(ratingText(line));
}

// A batch reviewed by the rev-ar / create-video-record pipeline records its
// verdict in Other Info, NOT as per-case annotations in Subject Matter — e.g.
// "HTML | 2026-07-03 | Reviewed: 7 | Excluded: 1 ..." or "2026-07-27: 2
// request(s) made". Those batches have a fully annotation-free Subject Matter
// and were being reported as "not reviewed" when they had in fact been
// reviewed end to end (15 of the 65 such pills in the 2026-07-25 export).
// A count is required before "request(s) made" so a hand-typed note like
// "no request made" doesn't read as a completed pass.
const FU_REVIEWED_ELSEWHERE = /\bReviewed:\s*\d+|\d+\s+request(?:\(s\)|s)?\s+made\b/i;

// The column-header row of a pasted list, e.g. "Name\tCase No.\tArrest Date".
// The previous check was anchored (/^(case no|incident #)/), so it only caught
// headers whose FIRST column was the case number. A header starting with "Name"
// survived — and then "Case No." matched \bno\b in FU_NEGATIVE, so the header
// itself was counted as a judged case. Measured on the 2026-07-25 export: 295
// header rows survived the anchored filter and 123 of them scored as an
// annotation. The damage was not just the off-by-one in the count: one fake
// negative makes `negative === 0` false, so the zero-annotation branch never
// runs, and with it the FU_REVIEWED_ELSEWHERE suppression never runs either.
// Batches reviewed end to end by the rev-ar pipeline therefore fell through to
// "N left" — 18 of them in that export. Match the label anywhere on the line,
// but only drop the line if it carries no real case number, so a genuine data
// row mentioning one is never discarded.
// Boundaries go on the word alternatives, never after "#": a trailing \b after
// "#" requires a word character next, so "Incident #<tab>Name" would not match.
const FU_HEADER = /\bcase\s*(?:no\b\.?|number\b|#)|\bincident\s*#/i;
// 2-4 digit year prefix: some agencies' lists use a 2-digit year ("25-06987"),
// others the full 4-digit year ("2025-00008312") — e.g. Vermilion County
// Sheriff, whose every Subject Matter line contains the literal text
// "Case No." inline before its (4-digit-year) case number. With the old
// \d{2}-only pattern, FU_CASENUM never matched those lines, so the FU_HEADER
// guard below saw "contains 'Case No.' with no recognized case number" and
// discarded every line in the batch as a fake header row — leaving `lines`
// empty and the whole pill silently suppressed regardless of what Subject
// Matter or Update actually said.
const FU_CASENUM = /\b\d{2,4}-\d{3,}\b/;
// The year a record covers, taken from its own name ("Stuart 2025 Arrests").
const FU_YEAR = /\b(20\d{2})\b/;
const FU_ARS_BATCH = /\bARs\s*(\d+)\b/i;

// ── Verdict figures the pipeline already writes into Other Info ────────────
// Both generators append one dated "HTML | ..." line per run and never
// overwrite, so the LAST occurrence is the current state.
//
//   /rev-cases  ... | Qualifying: 47 | Standouts: 12 | Batch: ARs 3 (8 cases)
//               | Remaining: 36 unmined | Claude Report: <url>
//   rev-ar      ... | Reviewed: 14 | Excluded: 3 (...) | Not Received: 1
//               | Flagged: 1 | Notable: 2 | AI Summaries: 14 | Report: <url>
const FU_UNMINED = /Remaining:\s*(\d+)\s*unmined/gi;
const FU_NOT_RECEIVED = /Not Received:\s*(\d+)/gi;
const FU_REVIEWED_COUNT = /Reviewed:\s*(\d+)/gi;
const FU_AI_SUMMARIES = /AI Summaries:\s*(\d+)/gi;
const FU_STANDOUTS = /Standouts:\s*(\d+)/gi;
const FU_QUALIFYING = /Qualifying:\s*(\d+)/gi;
const FU_REQUESTED = /Requested:\s*(\d+)/gi;

// The HTML report each generator links back to. Only an http(s) URL is usable
// as a link — CCM-App's formatAndLinkUrls() deliberately never hyperlinks
// file://, so a file:// value is captured but flagged unusable rather than
// rendered as a dead control. scripts/fix_report_links.py migrates those.
//
// The word before "Report:" is whichever assistant's environment generated
// the report — detect_generator_author() in generate_rev_ar_report.py /
// generate_rev_cases_report.py returns "Claude" only when CLAUDECODE=1 is
// set, "Gemini" otherwise (Antigravity, a scheduled run, or any session
// without that env var). Found 2026-09-07: 26 records had a real, fresh
// "Gemini Report: <url>" write-back sitting in Other Info that this regex
// silently failed to parse (only "Claude " was tolerated as a prefix),
// which meant reportLinkFrom() returned no link at all and every one of
// those records was invisible to Needs Review despite being fully
// screened. Matching both authors here is a read-side fix — it un-orphans
// every existing "Gemini Report:" write with no Firestore migration needed.
const FU_REPORT_LINK = /(?:(?:Claude|Gemini)\s+)?Report:\s*(\S+)/gi;

// Once a batch is reviewed elsewhere (FU_REVIEWED_ELSEWHERE above), its real
// dispositions come from the Update field's "rev-AR ratings synced ..."
// history (rev-ar.md's Rating Write-Back / the report's "Save Rating Only"
// button), not from Subject Matter — same source generate_rev_ar_report.py's
// own DONE auto-tag reads. Multiple dated lines can accumulate across runs;
// later lines override earlier ones for the same name, same rule that script
// uses. Names are "Last, First" (contain a comma), so pairs are split on "|"
// and each pair on its LAST "=" — matches _parse_synced_ratings in
// generate_rev_ar_report.py exactly.
// An older writer used a different shape: pairs joined by commas and the count
// as "| 8 rated" rather than "(8 rated)". Six records in the 2026-08-09 export
// still carry it, every one of them fully rated No — and because the parser
// could not read them they looked like batches nobody had ever rated, landing
// in "Rate rev-AR report" as work to do. Accept either terminator.
const FU_SYNCED_LINE = /rev-AR ratings synced [^:]*:\s*(.+?)\s*(?:\(\s*\d+\s+rated\s*\)|\|\s*\d+\s+rated)/gi;

// Names are stored "Last, First", so a bare comma split is ambiguous — which
// is exactly why the format moved to pipes. A comma that FOLLOWS a rating
// value is not ambiguous though, so normalise those into pipes first and the
// same split then handles both shapes.
const FU_RATING_COMMA = /\b(Yes|No|Maybe)\s*,\s*/gi;

// rev-ar labels a case "Case #<number>" (or "Report #<number>") when it
// couldn't extract a suspect name from the PDF — see rev-ar's "No name →
// Case #<number>" rule. That placeholder is what lands in the synced
// ratings text, but the real footage record (once requested) is always
// filed under the suspect's actual name, so nameTokensMatch can never link
// the two: neither name shares a token with the other. Fall back to
// case-number matching for placeholder ratings — rev-ar's own gist text
// always ends with "Case #<number>, Arrest Date: ...", so the number
// reliably shows up in the sibling record's Subject Matter.
const FU_CASE_PLACEHOLDER = /^(?:case|report)\s*#\s*(\d+)$/i;

// "DONE" in Other Info closes a batch — but the bare word says nothing about
// what precedes it, and "reviewed the first half, not done" means the exact
// opposite. Strip negated phrasings first, then look for a DONE that survives:
// "ARs 3 ordered. DONE" still closes, "almost done" no longer does. No record
// in the current export is phrased that way, which is precisely why this is
// worth adding now rather than after one is.
const FU_DONE = /\bDONE\b/i;
const FU_DONE_NEGATED = /\b(?:not|isn'?t|ain'?t|aren'?t|never|almost|nearly|hardly|barely|half|partly|partially)\s+done\b/gi;

export function isMarkedDone(otherInfo) {
  return FU_DONE.test(String(otherInfo || '').replace(FU_DONE_NEGATED, ' '));
}

// "STALE" in Other Info closes a batch exactly like DONE (CLAUDE.md: "treat
// it exactly like DONE... Any script or report logic that checks Other Info
// for DONE to skip/close a batch must check for STALE the same way"). Kept as
// a separate keyword/function only so the reason a batch closed (exhausted
// vs. deliberately deprioritized) stays visible to a human later — every
// consumer that gates on isMarkedDone() to mean "closed, no longer needs
// review" must also check isMarkedStale().
const FU_STALE = /\bSTALE\b/i;

export function isMarkedStale(otherInfo) {
  return FU_STALE.test(String(otherInfo || ''));
}

export function isMarkedClosed(otherInfo) {
  return isMarkedDone(otherInfo) || isMarkedStale(otherInfo);
}

// When rev-AR last ran, taken from the dated verdict line it writes
// ("HTML | 2026-06-22 | Reviewed: 8 | ..."). Read per line rather than
// "last date anywhere in the field", so an unrelated note such as
// "ARs 3 ordered on 2026-07-24" can't be mistaken for the run date.
const FU_ISO_DATE = /\b(20\d{2}-\d{2}-\d{2})\b/;
const FU_REQUESTS_MADE = /\d+\s+request(?:\(s\)|s)?\s+made\b/i;

export function revArRunDate(otherInfo) {
  let found = null;
  for (const line of String(otherInfo || '').split(/[\r\n]+/)) {
    if (!/Reviewed:\s*\d+/i.test(line)) continue;
    const m = FU_ISO_DATE.exec(line);
    if (m) found = m[1];
  }
  return found;
}

export function parseSyncedRatings(updateText) {
  const latest = new Map();
  const text = updateText || '';
  let m;
  FU_SYNCED_LINE.lastIndex = 0;
  while ((m = FU_SYNCED_LINE.exec(text))) {
    const payload = m[1].replace(FU_RATING_COMMA, '$1|');
    for (const pair of payload.split('|')) {
      const idx = pair.lastIndexOf('=');
      if (idx === -1) continue;
      const name = pair.slice(0, idx).trim();
      const rating = pair.slice(idx + 1).trim();
      if (name) latest.set(name, rating);
    }
  }
  return latest;
}

// A name synced as Yes/Maybe (e.g. "San-Nicolas, Christopher A.") can already
// have its own separate single-case footage record elsewhere in the
// database — rev-ar's own dedup check catches this on every fresh pass (it
// moves the case into REMOVED with source "database"), but that verdict
// lives in a re-run of the pipeline, not in the Update field's ratings
// history text, which is never edited after the fact. Cross-checking against
// sibling records for the same agency here closes that gap directly, instead
// of waiting on a future rev-ar refresh that the ratings-history read alone
// can never reflect.
function normalizeNameTokens(name) {
  if (!name) return [];
  const cleaned = String(name)
    .toLowerCase()
    .replace(/\([^)]*\)/g, ' ') // handled separately — see incidentDiscriminator
    .replace(/[^a-z\s]/g, ' ')  // drop punctuation/digits
    .replace(/\s+/g, ' ')
    .trim();
  return cleaned.split(' ').filter(Boolean);
}

// The bracket and the trailing number are NOT noise — they say WHICH incident.
// A person can have several arrests, and that is exactly how they are told
// apart: "Krystal Fowler (July 2025 Arrest)", "Jack Mobley 2 / 3 / 4",
// "April White (6 months later)", "Bussey, Latell (I) / (II)". Stripping them
// (the old comment called them "nicknames") made every incident for one person
// at one agency collapse into a single identity — 29 such groups in the
// current data — so a newly rated case could be silently suppressed because a
// DIFFERENT arrest of the same person already had a record.
//
// A bare two-letter code is a location qualifier, not an incident: "Aurora
// (CO)" must not be treated as a discriminator.
const FU_PAREN = /\(([^)]*)\)/g;
const FU_STATE_CODE = /^[A-Z]{2}$/;
const FU_TRAILING_NUM = /(?:^|\s)(\d+)\s*$/;

function incidentDiscriminator(name) {
  const raw = String(name || '');
  const out = new Set();
  FU_PAREN.lastIndex = 0;
  let m;
  while ((m = FU_PAREN.exec(raw))) {
    const inner = m[1].trim();
    if (!inner || FU_STATE_CODE.test(inner)) continue;
    out.add(inner.toLowerCase().replace(/[^a-z0-9]+/g, ''));
  }
  const trailing = FU_TRAILING_NUM.exec(raw.replace(/\([^)]*\)/g, ' '));
  if (trailing) out.add(trailing[1]);
  return out;
}

// Two discriminators are compatible only when they agree. If one name pins an
// incident down and the other does not, we cannot prove they are the same, so
// we do not claim it. That errs toward showing a row you can dismiss at a
// glance rather than hiding work you would never learn about — the asymmetry
// that matters here, since a suppressed case is invisible by construction.
function discriminatorsAgree(a, b) {
  const da = incidentDiscriminator(a);
  const db = incidentDiscriminator(b);
  if (!da.size && !db.size) return true;
  if (da.size !== db.size) return false;
  for (const x of da) if (!db.has(x)) return false;
  return true;
}

export function nameTokensMatch(a, b) {
  const setA = new Set(normalizeNameTokens(a));
  const setB = new Set(normalizeNameTokens(b));
  // Require at least first+last (2 tokens) so a single shared token (a common
  // last name, or "Case" from a "Case #N" placeholder) can't false-positive.
  if (setA.size < 2 || setB.size < 2) return false;
  const [small, big] = setA.size <= setB.size ? [setA, setB] : [setB, setA];
  for (const token of small) {
    if (!big.has(token)) return false;
  }
  // Same person — but the same INCIDENT? See incidentDiscriminator above.
  return discriminatorsAgree(a, b);
}

// Last match wins — these lines accumulate, and an older run's figure is
// stale by definition. Exported so consumers stop reimplementing it: the local
// copy in AIReportsView drifted within hours of being written, missing the
// legacy-verdict fix below.
// rev-AR's own "Reviewed: N" case count from Other Info (null when absent), and
// whether the batch's Other Info records that footage requests were made.
// Exported for Library's ARs status, which needs to tell "rated a few, then
// acted" (deliberate) from "rated a few, nothing done" (stalled).
export function reviewedCaseCount(otherInfo) {
  return lastFigure(FU_REVIEWED_COUNT, otherInfo || '');
}
export function hasRequestsMade(otherInfo) {
  return FU_REQUESTS_MADE.test(otherInfo || '');
}
// rev-AR's "Not Received: N" from its latest run (0 when absent): cases the
// agency hasn't sent yet, so a later installment is still expected.
export function notReceivedCount(otherInfo) {
  return lastFigure(FU_NOT_RECEIVED, otherInfo || '') || 0;
}

// How many more cases the latest rev-AR run reviewed than the batch had when
// its last footage request was made (2026-10-22, user: "If I add ARs to a
// batch, ... the report should be in Needs Review"). A partly rated batch with
// a request made reads Reviewed ("rated the ones I wanted, skipped the rest"),
// but ARs delivered after that request were never skipped, only never seen —
// Lakewood (WA) PD 2025 ARs 1 (7 -> 15 reviewed) and Blackfoot PD 2025 ARs 2
// (2 -> 6) in the 2026-10-22 export. 0 when nothing grew or there is no
// request line / earlier "Reviewed: N" to compare against.
export function arsAddedSinceLastRequest(otherInfo) {
  const lines = String(otherInfo || '').split(/[\r\n]+/);
  let lastRequest = -1;
  lines.forEach((line, i) => { if (FU_REQUESTS_MADE.test(line)) lastRequest = i; });
  if (lastRequest === -1) return 0;
  const before = lastFigure(FU_REVIEWED_COUNT, lines.slice(0, lastRequest + 1).join('\n'));
  const latest = reviewedCaseCount(otherInfo);
  if (before === null || latest === null) return 0;
  return Math.max(0, latest - before);
}

export function lastFigure(re, text) {
  if (!text) return null;
  re.lastIndex = 0;
  let m, last = null;
  while ((m = re.exec(text))) last = m[1];
  return last === null ? null : parseInt(last, 10);
}

function lastMatchRaw(re, text) {
  if (!text) return null;
  re.lastIndex = 0;
  let m, last = null;
  while ((m = re.exec(text))) last = m[1];
  return last;
}

// Pulls the stored "Report: <url>" link out of Other Info, independent of
// follow-up state — decorate() below needs it for records with outstanding
// work, and LibraryView needs it for EVERY report-bearing record including
// ones computeFollowUp() has gone silent on (fully rated, nothing left to
// request). Two callers, one parse, so the regex and the http(s)-only rule
// can't drift between them.
export function reportLinkFrom(otherInfo) {
  const rawLink = lastMatchRaw(FU_REPORT_LINK, otherInfo || '');
  return {
    reportUrl: rawLink && /^https?:\/\//i.test(rawLink) ? rawLink : null,
    reportUnlinkable: !!(rawLink && !/^https?:\/\//i.test(rawLink)),
  };
}

// Has /rev-cases screened this list, and how much is left?
//
// The current format records "Remaining: N unmined" directly. An older one
// did not — it wrote a dated preamble plus "Qualifying: N, Ceiling: N,
// Standouts: N, Requested: N", where what is left is simply what qualified
// minus what was already ordered. One record in the 2026-08-08 export still
// carries that shape (Joliet 2025 Arrests, 10 qualifying / 10 requested), and
// reading only the modern figure made it look never-screened, so it showed a
// false "Run rev-cases" row that no amount of re-screening would clear.
//
// Safe to key on Qualifying: it appears on 117 records, only one of which
// lacks Remaining, and "Requested:" never appears without it.
// How many times rev-cases has actually run on this list. Every real run's
// STEP 5 write-back appends exactly one NEW "Qualifying: N" line and never
// removes an old one (rev-cases.md STEP 5: "append a new dated entry ... if
// it already has prior HTML entries"), so counting them tells "first-ever
// screening" (1) apart from "this list has been refreshed/re-run before"
// (2+) — independent of whether any PRIOR run was ever confirmed via Save
// Picks or marked Done/Stale. See useReportStatus.js's Refreshed state: an
// active agency's list may never reach Done/Stale (it just keeps growing
// every re-run) or ever get Save-Picks-clicked, but it's still been
// genuinely refreshed, and that's the state worth calling out.
//
// A line marked "no new source rows" is not counted (2026-10-17): those are
// maintenance re-runs that only rebuilt the report file (38 of them on
// 2026-10-10, after the already-requested fix), not a new look at new material.
export function revCasesRunCount(otherInfo) {
  return String(otherInfo || '')
    .split('\n')
    .filter((line) => /Qualifying:\s*\d+/i.test(line) && !/no new source rows/i.test(line))
    .length;
}

// The latest rev-cases run's date and suggested-batch size, from its STEP 5
// line ("HTML | 2026-10-17 | ... | Batch: ARs 7 (8 cases) | ..."). Unlike the
// run count, "no new source rows" re-runs count here: a re-run still applies
// the live already-requested flags, so its batch is what is left unpulled.
const FU_BATCH_COUNT = /Batch:[^|]*\((\d+)\s+cases?\)/i;
export function revCasesLastRun(otherInfo) {
  let found = { date: null, batchCount: null };
  for (const line of String(otherInfo || '').split(/[\r\n]+/)) {
    if (!/Qualifying:\s*\d+/i.test(line)) continue;
    const d = FU_ISO_DATE.exec(line);
    if (!d) continue;
    const b = FU_BATCH_COUNT.exec(line);
    found = { date: d[1], batchCount: b ? parseInt(b[1], 10) : null };
  }
  return found;
}

export function revCasesVerdict(otherInfo) {
  const modern = lastFigure(FU_UNMINED, otherInfo);
  if (modern !== null) return { screened: true, remaining: modern };
  const qualifying = lastFigure(FU_QUALIFYING, otherInfo);
  if (qualifying === null) return { screened: false, remaining: null };
  const requested = lastFigure(FU_REQUESTED, otherInfo) || 0;
  return { screened: true, remaining: Math.max(0, qualifying - requested) };
}

export function agencyKeyOf(item) {
  return (item?.agencyName || item?.Agency || '').trim().toLowerCase();
}

// The canonical set of follow-up states. Action Center ranks on these, so the
// order here is the priority order: a decision already made outranks work not
// yet started.
export const FOLLOW_UP_KINDS = [
  'to_request',      // yes/maybe decided, no request filed  → file it
  'unmined',         // rev-cases pool with cases left       → pull next ARs batch
  'unrated_report',  // rev-AR ran, nothing ever decided     → rate it
  'unexpanded',      // rev-AR ran with --top N              → re-run to expand
  'needs_rev_cases', // annual list in hand, never screened  → run rev-cases
  'not_reviewed',    // no annotations at all                → read + annotate
  'partial',         // some annotated, some blank           → finish annotating
  'unmined_held',    // pool left, but a batch is already out with the agency
  'withheld',        // agency didn't send everything asked for
  'done',            // confirmed closed
];

export function useFollowUpState(allRecords) {
  const recordsByAgencyName = computed(() => {
    const map = new Map();
    for (const r of allRecords.value || []) {
      const key = agencyKeyOf(r);
      if (!key) continue;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    }
    return map;
  });

  // Is this agency already waiting on an ARs batch it hasn't received yet?
  // Mirrors the weekly report's "Outstanding ARs Request Check": while a batch
  // is out, surfacing "there are more cases to mine here" is premature — the
  // next batch can't be ordered until this one lands.
  function outstandingArsBatch(item) {
    const candidates = recordsByAgencyName.value.get(agencyKeyOf(item)) || [];
    return candidates.find(
      (r) => r.id !== item.id
        && getInfoBucket(r) === 'ars'
        && ['Pending', 'Action'].includes((r.stage || '').trim()),
    ) || null;
  }

  // Does this Subject Matter case LINE already have its own footage record?
  //
  // The synced-ratings path has always cross-checked this. The Subject Matter
  // path never did — so a case annotated YES stayed "to request" forever, even
  // after the request was filed, because the annotation is almost never edited
  // to REQUESTED once the separate record exists. Measured on the 2026-08-08
  // export: 56 of 99 ready lines (57%) already had a record, which made the
  // top tier of Action Center more than half noise.
  //
  // A line is a whole data row — case number, date, name, race, sex, charges —
  // so the test is the other way round from a name-to-name match: every token
  // of the sibling record's NAME must appear somewhere in the line.
  function lineAlreadyRequested(item, line) {
    const lineTokens = new Set(
      String(line || '').toLowerCase().replace(/[^a-z\s]/g, ' ').split(/\s+/).filter(Boolean),
    );
    const siblings = recordsByAgencyName.value.get(agencyKeyOf(item)) || [];
    return siblings.some((r) => {
      if (r.id === item.id) return false;
      // Only a real footage request counts — another ARs batch is not one.
      if (getRecordType(r) !== 'footage') return false;
      const nameTokens = normalizeNameTokens(r.name);
      if (nameTokens.length < 2) return false;
      return nameTokens.every((t) => lineTokens.has(t));
    });
  }

  function alreadyHasOwnRecord(item, ratingName) {
    const candidates = recordsByAgencyName.value.get(agencyKeyOf(item)) || [];
    const placeholder = FU_CASE_PLACEHOLDER.exec((ratingName || '').trim());
    if (placeholder) {
      const caseNo = placeholder[1];
      const caseNoRe = new RegExp(`\\b${caseNo}\\b`);
      return candidates.some((r) => r.id !== item.id && caseNoRe.test(r.subjectMatter || ''));
    }
    return candidates.some((r) => r.id !== item.id && nameTokensMatch(r.name, ratingName));
  }

  // Secondary states, read from the verdict figures rev-ar and /rev-cases
  // already write. These sit BELOW "N to request" in priority — that one is a
  // decision already made and waiting to be acted on, these are earlier in the
  // funnel — but above silence, which is what they used to get.
  function verdictState(item) {
    const oi = item.otherInfo || '';

    // rev-AR ran with --top N, so some cases never got a gist and
    // /create-video-record can't build a record for them. Re-running rev-ar
    // without --top is the fix.
    const reviewed = lastFigure(FU_REVIEWED_COUNT, oi);
    const summaries = lastFigure(FU_AI_SUMMARIES, oi);
    if (reviewed !== null && summaries !== null && summaries < reviewed) {
      return { kind: 'unexpanded', count: reviewed - summaries, total: reviewed, expanded: summaries };
    }

    // The agency withheld reports from what it sent back. Nothing else in the
    // app surfaces this at all.
    const withheld = lastFigure(FU_NOT_RECEIVED, oi);
    if (withheld) return { kind: 'withheld', count: withheld };
    return null;
  }

  // Which ARs batch number comes next for this agency and year. Batch numbers
  // restart each year, so only same-year siblings count. An unnumbered
  // "Agency 2024 ARs" is batch 1 by convention. Used to name the next batch in
  // the /gen-email-ars-request command without having to go and look it up.
  function nextArsBatchNumber(item) {
    const yearMatch = FU_YEAR.exec(item.name || '');
    const year = yearMatch ? yearMatch[1] : null;
    let highest = 0;
    for (const r of recordsByAgencyName.value.get(agencyKeyOf(item)) || []) {
      if (getInfoBucket(r) !== 'ars') continue;
      const name = r.name || '';
      if (year && !name.includes(year)) continue;
      const m = FU_ARS_BATCH.exec(name);
      highest = Math.max(highest, m ? parseInt(m[1], 10) : 1);
    }
    return highest + 1;
  }

  // An annual list (Arrests/UOF/Complaints) that /rev-cases has reviewed, where
  // qualifying cases remain that were never pulled into an ARs batch. This is
  // the largest untapped pool in the whole pipeline and had no surface anywhere.
  function unminedState(item) {
    if (!['logs', 'uof', 'complaints'].includes(getInfoBucket(item))) return null;
    const unmined = revCasesVerdict(item.otherInfo || '').remaining;
    if (!unmined) return null;
    const blocker = outstandingArsBatch(item);
    return {
      // Don't nag while a batch is already out with the agency — the next one
      // can't be ordered until that one lands.
      kind: blocker ? 'unmined_held' : 'unmined',
      count: unmined,
      nextBatch: nextArsBatchNumber(item),
      standouts: lastFigure(FU_STANDOUTS, item.otherInfo || ''),
      qualifying: lastFigure(FU_QUALIFYING, item.otherInfo || ''),
      blockedBy: blocker ? { kind: 'outstanding-ars', batch: blocker.name, stage: blocker.stage } : null,
    };
  }

  // An annual list (Arrests/UOF/Complaints) that is in hand but has never been
  // through /rev-cases at all — no "Remaining: N unmined" verdict, no rev-AR
  // marker. This is the weekly report's Section 2B, and until now it had no
  // surface anywhere: 79 such records carry no parseable Subject Matter lines
  // either (the list arrived as a PDF in the library and was never pasted in),
  // so every earlier branch fell through and the row went completely silent.
  //
  // Deliberately checked ONLY where the old code returned null. A list that
  // does have pasted lines keeps its existing not_reviewed/partial pill, so no
  // row on the Records page changes meaning — Action Center still routes those
  // to rev-cases via nextActionFor(), which reads the bucket.
  function needsRevCasesState(item) {
    if (!['logs', 'uof', 'complaints'].includes(getInfoBucket(item))) return null;
    const oi = item.otherInfo || '';
    // revCasesVerdict(), not a bare .test() — FU_UNMINED carries the /g flag,
    // so .test() advances its lastIndex and returns false on every other call,
    // and the verdict may be in the older Qualifying/Requested form with no
    // Remaining figure at all. FU_REVIEWED_ELSEWHERE has no /g and is safe.
    if (revCasesVerdict(oi).screened || FU_REVIEWED_ELSEWHERE.test(oi)) return null;
    const blocker = outstandingArsBatch(item);
    return {
      kind: 'needs_rev_cases',
      blockedBy: blocker ? { kind: 'outstanding-ars', batch: blocker.name, stage: blocker.stage } : null,
    };
  }

  function computeFollowUp(item) {
    // Only informational list records carry per-case annotations.
    if (getRecordType(item) !== 'informational') return null;
    // "DONE" in Other Info means fully closed — surface that positively
    // instead of just going silent, so a closed batch reads as "confirmed
    // done" rather than "nothing detected here". No stage restriction (unlike
    // the checks below): a closed batch can sit at Received, Bullpen, Kill,
    // or No Footage and DONE means the same thing in all of them.
    if (isMarkedDone(item.otherInfo)) return decorate(item, { kind: 'done' });
    if (isMarkedStale(item.otherInfo)) return decorate(item, { kind: 'stale' });
    // The list has to actually be in hand.
    if (!['Received', 'Bullpen', 'BullpenX'].includes((item.stage || '').trim())) return null;

    // Once the rev-ar / create-video-record pipeline has reviewed this batch,
    // its verdict (dedup exclusions against the database, "N request(s) made")
    // lives in Other Info/Update, NOT as per-case annotations in Subject
    // Matter — so Subject Matter's own yes/maybe/no text can no longer be
    // trusted as "what's actually still outstanding" (Orland 2025 ARs 1 showed
    // "7 to request" from hand-typed Subject Matter text alone, while its
    // Other Info recorded 6 of 9 cases as database duplicates and 2 more as
    // already requested — at most 1 real case). This check runs BEFORE any
    // Subject Matter parsing below, not just before the old zero-annotation
    // branch — Subject Matter's own formatting (tabs, headers, case-number
    // patterns) is irrelevant once Other Info says the pipeline owns this
    // batch's verdict, and depending on it needlessly made this fragile: e.g.
    // Vermilion County Sheriff 2025 ARs 6's lines all contain the literal text
    // "Case No." inline, which used to make every line look like a fake header
    // row (see FU_CASENUM above) and return null before this check ever ran.
    //
    // Going straight to silence here would still throw out real signal,
    // though: ratings entered through the report's "Save Rating Only" button
    // land in the Update field as "rev-AR ratings synced ..." (see
    // FU_SYNCED_LINE above), and a Yes/Maybe there that hasn't yet produced a
    // request is exactly the "follow up on this" state this pill exists for —
    // e.g. Vermilion synced Allen, Emma M=Yes (8 others No) with no video
    // record made yet. So check Update's synced-ratings history, same "most
    // recent rating per name wins" rule generate_rev_ar_report.py's own DONE
    // auto-tag uses, and only fall back to no pill if nothing there is
    // outstanding either. A name here can already have its own separate
    // single-case record (rev-ar's dedup catches this on a fresh pass, but
    // that verdict never edits this ratings-history text after the fact) —
    // alreadyHasOwnRecord cross-checks sibling records for the same agency so
    // that a name already turned into a request doesn't still read as
    // outstanding here.
    if (FU_REVIEWED_ELSEWHERE.test(item.otherInfo || '')) {
      const synced = parseSyncedRatings(item.update);
      const readyNames = [...synced.entries()]
        .filter(([, r]) => r === 'Yes' || r === 'Maybe')
        .filter(([name]) => !alreadyHasOwnRecord(item, name));
      if (readyNames.length > 0) {
        return decorate(item, {
          kind: 'to_request',
          count: readyNames.length,
          source: 'ratings',
          ratings: readyNames.map(([name, rating]) => ({ name, rating })),
        });
      }
      // Nothing left to request — but "reviewed" is not the same as "finished".
      // Two states below it were previously invisible, and both are real work.
      const verdict = verdictState(item);
      if (verdict) return decorate(item, verdict);

      // rev-AR read the PDFs, ranked the cases and wrote the gists — and then
      // nothing was ever decided: no rating synced, no request filed. The most
      // stranded value in the pipeline, since the expensive half is already
      // paid for and the payoff is zero until someone spends ten minutes in
      // the report. Six such batches (~42 ranked cases) had no surface at all
      // before this — they fell through to null and showed nowhere.
      const reviewed = lastFigure(FU_REVIEWED_COUNT, item.otherInfo || '');
      if (reviewed && synced.size === 0 && !FU_REQUESTS_MADE.test(item.otherInfo || '')) {
        return decorate(item, {
          kind: 'unrated_report',
          count: reviewed,
          runDate: revArRunDate(item.otherInfo),
        });
      }
      return null;
    }

    const raw = item.subjectMatter || '';
    const lines = raw
      .split(/[\r\n]+/)
      .map((l) => l.trim())
      .filter((l) => l && !(FU_HEADER.test(l) && !FU_CASENUM.test(l)));
    // No parseable case lines at all — but Other Info may still carry a verdict
    // (a rev-cases run on a list whose Subject Matter was never pasted in).
    if (!lines.length) {
      return decorate(item, unminedState(item) || verdictState(item) || needsRevCasesState(item));
    }

    let ready = 0, handled = 0, negative = 0;
    const readyLines = [];
    for (const l of lines) {
      if (FU_YESMAYBE.test(l) && !FU_HANDLED.test(l)) { ready++; readyLines.push(l); }
      else if (FU_HANDLED.test(l)) handled++;
      else if (isNegativeLine(l)) negative++;
    }
    const unannotated = lines.length - ready - handled - negative;

    // Highest priority: cases already judged worth pursuing that were never
    // requested. `ready` above stays the raw annotated count (those lines ARE
    // annotated, so the unannotated arithmetic must not change), but only the
    // ones with no separate record yet are actually outstanding.
    const outstandingLines = readyLines.filter((l) => !lineAlreadyRequested(item, l));

    // Every case in the batch, classified, so the detail pane can show WHICH
    // cases a count refers to instead of asking for the number to be trusted.
    // `requested` is only resolved for ready lines — it is the only place the
    // answer changes anything, and it costs a sibling scan per line.
    const caseLines = lines.map((l) => {
      const isReady = FU_YESMAYBE.test(l) && !FU_HANDLED.test(l);
      return {
        text: l,
        label: isReady ? 'ready'
          : FU_HANDLED.test(l) ? 'handled'
            : isNegativeLine(l) ? 'no' : 'unannotated',
        requested: isReady ? lineAlreadyRequested(item, l) : null,
      };
    });
    if (outstandingLines.length > 0) {
      return decorate(item, {
        kind: 'to_request',
        count: outstandingLines.length,
        total: lines.length,
        source: 'annotation',
        // Carried so the detail pane can show WHICH cases, rather than leaving
        // a bare number to be taken on trust.
        caseLines,
      });
    }
    // Once /rev-cases has recorded a verdict for this list, its own
    // "Remaining: N unmined" figure beats anything inferred from Subject
    // Matter's annotations — same reasoning as the reviewed-elsewhere branch
    // above. A pasted arrest log with no annotations is not "not reviewed" if
    // rev-cases already screened all 1,200 rows of it and kept 47.
    const unmined = unminedState(item);
    if (unmined) return decorate(item, unmined);

    // An annual list whose next step is rev-cases is blocked by exactly the
    // same rule as an un-mined pool: screening it produces candidates for an
    // ARs batch that cannot be ordered while the last one is still out. These
    // two states never carried a blocker, so 34 such lists read as actionable
    // while their agency still owed a batch. ARs batches are deliberately
    // exempt — reviewing a batch already in hand is not blocked by anything.
    const listBlocker = ['logs', 'uof', 'complaints'].includes(getInfoBucket(item))
      ? outstandingArsBatch(item)
      : null;
    const blockedBy = listBlocker
      ? { kind: 'outstanding-ars', batch: listBlocker.name, stage: listBlocker.stage }
      : null;

    // Never reviewed: list received but no annotations at all. A part-reviewed
    // list still falls through to "N left" below, because a mixed state is
    // genuinely ambiguous.
    if (handled === 0 && negative === 0 && unannotated >= 3) {
      return decorate(item, {
        kind: 'not_reviewed', count: unannotated, total: lines.length, blockedBy, caseLines,
      });
    }
    // Part-reviewed: some lines judged, others still bare.
    if (unannotated > 0) {
      return decorate(item, {
        kind: 'partial', count: unannotated, total: lines.length,
        done: handled + negative, blockedBy, caseLines,
      });
    }
    return decorate(item, verdictState(item));
  }

  // Attaches the fields every consumer needs regardless of which branch
  // produced the state, so callers never re-parse Other Info themselves.
  function decorate(item, state) {
    if (!state) return null;
    const oi = item.otherInfo || '';
    // rev-cases ran on this list and reported nothing left to mine. The blank
    // lines still sitting in Subject Matter are cases it already screened and
    // rejected, so a zero remainder is a finished pool, not an unstarted one —
    // without this, 5 fully-mined lists were being told to run rev-cases again.
    const verdict = revCasesVerdict(oi);
    // 0 is neutral/unscreened (CLAUDE.md: blank/0 means unrated, not bad) and
    // -1 is the separate "Unknown" flag — neither suppresses run_rev_cases. A
    // rating of 1 or 1/2 star, though, means the log itself was looked at and
    // judged not worth mining, same call as typing STALE into Other Info,
    // just made through the star widget instead. Lives here, not in any one
    // state builder, because 'needs_rev_cases', 'not_reviewed' and 'partial'
    // all funnel through revCasesOrWait() below to reach that same action.
    const lowRated = item.rating > 0 && item.rating <= 1;
    return {
      ...state,
      poolExhausted: verdict.screened && !verdict.remaining,
      lowRated,
      count: state.count ?? null,
      recordId: item.id,
      recordName: item.name,
      agencyKey: agencyKeyOf(item),
      bucket: getInfoBucket(item),
      blockedBy: state.blockedBy ?? null,
      // A file:// link exists but cannot be hyperlinked. Surfaced separately so
      // the UI can say "report exists, link needs migrating" rather than
      // pretending there is no report.
      ...reportLinkFrom(oi),
    };
  }

  // The template reads five properties off this per row, so it runs five times
  // per visible record — and it parses Other Info and cross-checks sibling
  // records on top of the Subject Matter scan. Memoize per record id, rebuilt
  // whenever the record set is replaced (allRecords is a shallowRef that
  // filterData swaps wholesale, so an edit invalidates it).
  const memo = computed(() => {
    void allRecords.value;
    void recordsByAgencyName.value;
    return new Map();
  });

  function followUpFor(item) {
    if (!item || !item.id) return computeFollowUp(item);
    const cache = memo.value;
    if (!cache.has(item.id)) cache.set(item.id, computeFollowUp(item));
    return cache.get(item.id);
  }

  // Every record with a non-null state, evaluated once. Action Center needs the
  // whole set at once; going through followUpFor() row by row would leave the
  // memo cold on first paint and re-scan siblings 2,900 times.
  const allFollowUps = computed(() => {
    const out = [];
    for (const r of allRecords.value || []) {
      const state = followUpFor(r);
      if (state) out.push({ record: r, state });
    }
    return out;
  });

  return { followUpFor, allFollowUps, outstandingArsBatch };
}

// ─── Presentation ──────────────────────────────────────────────────────────
// Kept separate from the state machine above: the Records page renders these
// as row pills, while Action Center groups and ranks on `kind` directly.
const PILLS = {
  done: () => ({
    icon: 'mdi-check-circle',
    color: 'green-darken-2',
    label: 'Done',
    tooltip: 'Fully closed — every case reviewed and either passed on or already requested.',
  }),
  stale: () => ({
    icon: 'mdi-archive-clock-outline',
    color: 'grey-darken-1',
    label: 'Stale',
    tooltip: 'Deliberately deprioritized, not fully worked — not worth revisiting.',
  }),
  to_request: (s) => ({
    icon: 'mdi-bell-alert',
    color: 'red-darken-2',
    label: `${s.count} to request`,
    tooltip: s.source === 'ratings'
      ? `${s.count} case${s.count === 1 ? '' : 's'} rated yes/maybe in synced rev-AR ratings, no request yet.`
      : `${s.count} case${s.count === 1 ? '' : 's'} marked yes/maybe with no request yet`
        + ` — of ${s.total} in this list.`,
  }),
  unmined: (s) => ({
    icon: 'mdi-pickaxe',
    color: 'teal-darken-2',
    label: `${s.count} un-mined`,
    tooltip: `${s.count} qualifying case${s.count === 1 ? '' : 's'} identified by rev-cases `
      + `and never requested. Pull the next ARs batch from the report in Other Info.`,
  }),
  unmined_held: (s) => ({
    icon: 'mdi-timer-sand',
    color: 'blue-grey-darken-1',
    label: `${s.count} un-mined`,
    tooltip: `${s.count} qualifying case${s.count === 1 ? '' : 's'} left in this list, `
      + `but an ARs batch is already out with this agency — wait for it to land.`,
  }),
  unexpanded: (s) => ({
    icon: 'mdi-text-box-search-outline',
    color: 'deep-purple-darken-1',
    label: `${s.count} unexpanded`,
    tooltip: `rev-AR expanded ${s.expanded} of ${s.total} cases (--top N). `
      + `The other ${s.count} ${s.count === 1 ? 'has' : 'have'} no narrative summary yet, `
      + `so ${s.count === 1 ? 'it' : 'they'} can't be turned into a request until `
      + `rev-AR is re-run without --top.`,
  }),
  withheld: (s) => ({
    icon: 'mdi-file-remove-outline',
    color: 'orange-darken-3',
    label: `${s.count} withheld`,
    tooltip: `${s.count} requested report${s.count === 1 ? '' : 's'} never arrived `
      + `— open investigation, redaction, or no reason given. See Other Info.`,
  }),
  // "N NEW", not the old countless "not reviewed": the two states this pill
  // sits beside both carry a number ("N left", "N to request"), so a bare
  // label was the one row you had to open to find out how much was in it. And
  // "left" implies a pass already started — the distinction that matters on a
  // batch received today is untouched vs part-worked, not how many remain.
  not_reviewed: (s) => ({
    icon: 'mdi-eye-off-outline',
    color: 'amber-darken-3',
    label: `${s.count} NEW`,
    tooltip: s.count === s.total
      ? `${s.total} case${s.total === 1 ? '' : 's'} in this batch, none rated yet.`
      : `${s.count} of ${s.total} cases have never been rated.`,
  }),
  partial: (s) => ({
    icon: 'mdi-progress-check',
    color: 'blue-grey-darken-1',
    label: `${s.count} left`,
    tooltip: `${s.count} of ${s.total} cases not yet rated (${s.done} done).`,
  }),
  unrated_report: (s) => ({
    icon: 'mdi-star-outline',
    color: 'purple-darken-2',
    label: `${s.count} unrated`,
    tooltip: `rev-AR ranked ${s.count} case${s.count === 1 ? '' : 's'} in this batch`
      + `${s.runDate ? ` on ${s.runDate}` : ''}, but nothing was ever rated and no request `
      + 'was filed. Open the report and rate them.',
  }),
  needs_rev_cases: (s) => ({
    icon: 'mdi-file-search-outline',
    color: 'indigo-darken-2',
    label: 'run rev-cases',
    tooltip: s.blockedBy
      ? `This annual list has never been screened by rev-cases, but an ARs batch `
        + `(${s.blockedBy.batch}) is already out with this agency — wait for it to land.`
      : `This annual list is in hand but has never been screened by rev-cases. `
        + `Run it to find qualifying cases for the next ARs batch.`,
  }),
};

export function followUpPill(state) {
  if (!state) return null;
  const build = PILLS[state.kind];
  return build ? build(state) : null;
}

// The specific tool or physical action each state calls for. Action Center's
// "Next Action" column — named per CLAUDE.md's Display rule, which bans the
// generic phrase "AI review" in favour of the actual tool name.
export const NEXT_ACTIONS = {
  file_request:  { label: 'Request footage',      icon: 'mdi-video',                   color: 'red-darken-2',          order: 0 },
  // Second only to filing a decided case: the analysis is bought and paid for
  // and returns nothing until it is rated, and rating it feeds the tier above.
  rate_report:   { label: 'Rate rev-AR report',   icon: 'mdi-star-outline',            color: 'purple-darken-2',       order: 1 },
  pull_batch:    { label: 'Request ARs',          icon: 'mdi-pickaxe',                 color: 'teal-darken-2',         order: 2 },
  expand_rev_ar: { label: 'Re-run rev-AR',        icon: 'mdi-text-box-search-outline', color: 'deep-purple-darken-1',  order: 3 },
  run_rev_ar:    { label: 'Run rev-AR',           icon: 'mdi-robot-outline',           color: 'blue-darken-2',         order: 4 },
  run_rev_cases: { label: 'Run rev-cases',        icon: 'mdi-file-search-outline',     color: 'indigo-darken-2',       order: 5 },
  annotate:      { label: 'Finish rating ARs',    icon: 'mdi-progress-check',          color: 'blue-grey-darken-1',    order: 6 },
  waiting:       { label: 'Waiting on agency',    icon: 'mdi-timer-sand',              color: 'blue-grey-darken-1',    order: 8 },
};

// ─── Pasteable commands ────────────────────────────────────────────────────
// Rows whose next action is "run a tool" have no report to open yet — that's
// the point of the action. What they can offer instead is the exact command to
// paste into Claude Code, built from the record itself so there's no retyping
// and no guessing at the agency shorthand.
//
// Syntax comes from .claude/commands/:
//   /rev-cases [agency] [year] [Arrests|UOF|Complaints]
//   rev-ar <agency> [year] [batch]
// Exported alongside agencyShorthand — one copy of the command syntax, shared
// by Action Center's commandFor() below and Needs Review's runCommandFor()
// in useReportStatus.js.
export const CMD_TYPE = { logs: 'Arrests', uof: 'UOF', complaints: 'Complaints' };

// Why a row has no command, for the actions where that is the correct answer
// rather than a gap. Without this the Run column shows a bare dash on the
// first 107 rows — the default sort leads with the two tiers that need a
// human step — and reads as a missing value instead of "nothing to run here".
// What the Cases column is counting on a given row. The number means something
// different in every tier — cases awaiting a request, cases left to mine, lines
// left to annotate, reports never sent — so a bare integer under one header is
// ambiguous by construction. Each row says what its own number is.
export function casesMeaning(state) {
  if (!state) return null;
  const n = state.count;
  switch (state.kind) {
    case 'to_request':
      return `${n} case${n === 1 ? '' : 's'} rated yes/maybe with no footage request filed yet`
        + (state.source === 'ratings' ? ', from synced rev-AR ratings.' : `, of ${state.total} in this batch.`);
    case 'unmined':
    case 'unmined_held':
      return `${n} qualifying case${n === 1 ? '' : 's'} rev-cases identified in this list `
        + 'and never pulled into an ARs batch.';
    case 'unexpanded':
      return `${n} of ${state.total} reviewed case${state.total === 1 ? '' : 's'} have no narrative `
        + 'summary yet, because rev-AR last ran with --top N.';
    case 'not_reviewed':
      return `${n} of ${state.total} cases have no rating yet.`;
    case 'partial':
      return `${n} of ${state.total} cases are still unrated (${state.done} decided).`;
    case 'withheld':
      return `${n} requested report${n === 1 ? '' : 's'} never arrived — open investigation, `
        + 'redaction, or no reason given. See Other Info.';
    case 'unrated_report':
      return `rev-AR ranked ${n} case${n === 1 ? '' : 's'} here`
        + `${state.runDate ? ` on ${state.runDate}` : ''} and none were rated. `
        + 'Open the report to score them yes/maybe/no.';
    case 'needs_rev_cases':
      // The dash here is not a zero. Nothing has been counted because the list
      // has no case rows pasted into Subject Matter — which is part of why it
      // still needs screening.
      return 'No case rows pasted into this record, so there is nothing to count yet — '
        + 'the list itself is what rev-cases will screen. A dash here is not zero.';
    default:
      return null;
  }
}

// Why a row's detail pane is empty. Not every state has per-case data in
// Firestore — for most of these the cases live in the HTML report, which the
// app cannot read. Clicking such a row and getting silence reads as a broken
// feature, so say where the cases actually are instead of rendering nothing.
export const NO_CASE_DETAIL_REASON = {
  unrated_report: 'The ranked cases live in the rev-AR report, not in this record. '
    + 'Open it with the Report button to see and rate them.',
  unexpanded: 'The ranked cases live in the rev-AR report, not in this record. '
    + 'Open it with the Report button.',
  unmined: 'The qualifying cases live in the rev-cases report, not in this record. '
    + 'Open it with the Report button to pick the next batch.',
  unmined_held: 'The qualifying cases live in the rev-cases report, not in this record.',
  needs_rev_cases: 'No case rows were pasted into this record — which is part of why '
    + 'it still needs screening. rev-cases reads the source list from the library.',
  withheld: 'See Other Info on the record for what the agency held back.',
};

export const NO_COMMAND_REASON = {
  file_request: 'No command — this needs an email to the agency naming the specific '
    + 'cases you want footage for. Open the batch to pick them.',
  annotate: 'No command — read the remaining cases and rate them yourself.',
  waiting: 'Nothing to run until the outstanding ARs batch lands.',
  pull_batch: 'No command — most agencies can\'t be emailed. Check Agency Info for '
    + 'this agency\'s request channel: /gen-email-ars-request if it has an email on '
    + 'file, otherwise submit through its portal, then run /create-ars-record either way.',
};

// rev-cases and rev-ar both resolve the agency by fuzzy-matching a library
// folder name. The batch's OWN name is the better source for that than the
// full agencyName — batches are named with the same shorthand the folders use
// ("Belleville 2024 ARs 2" → "Belleville", where agencyName is "Belleville
// PD"). Everything before the year is that shorthand.
// Exported so useReportStatus.js's Needs Review "copy command" icon can reuse
// the exact same shorthand rather than re-deriving it — see runCommandFor()
// there.
export function agencyShorthand(record) {
  const name = record?.name || '';
  const m = FU_YEAR.exec(name);
  const lead = m ? name.slice(0, m.index).trim() : '';
  const shorthand = lead || (record?.agencyName || '').trim();
  // Quote multi-word agencies so "[agency] [year]" stays unambiguous.
  return /\s/.test(shorthand) ? `"${shorthand}"` : shorthand;
}

export function commandFor(state, record) {
  if (!state || !record) return null;
  const action = nextActionFor(state);
  const agency = agencyShorthand(record);
  if (!agency) return null;

  const yearMatch = FU_YEAR.exec(record.name || '');
  const year = yearMatch ? yearMatch[1] : '';

  if (action === 'run_rev_cases') {
    const type = CMD_TYPE[state.bucket] || '';
    return `/rev-cases ${agency} ${year} ${type}`.replace(/\s+/g, ' ').trim();
  }
  // rate_report gets one too: the action is "open and rate", but these reports
  // are often in an older format and the fix is the same rev-ar re-run that
  // expand_rev_ar uses. A plain re-run reuses _analysis_cache.json, so it
  // rebuilds the HTML without re-reading the PDFs.
  if (action === 'run_rev_ar' || action === 'expand_rev_ar' || action === 'rate_report') {
    const batchMatch = FU_ARS_BATCH.exec(record.name || '');
    const batch = batchMatch ? batchMatch[1] : '';
    return `rev-ar ${agency} ${year} ${batch}`.replace(/\s+/g, ' ').trim();
  }
  // pull_batch deliberately has NO command, even though the batch number is
  // fully derivable (see nextArsBatchNumber) — unlike run_rev_cases/rev_ar,
  // /gen-email-ars-request only works for the ~30% of agencies with an email
  // on file (CLAUDE.md, gen-email-ars-request.md); this state machine has no
  // access to AgencyInfo to tell which. A command that's wrong 70% of the
  // time is worse than an honest "no command" — see NO_COMMAND_REASON.
  return null; // every other action is a human step, not a command
}

// Which action a state calls for depends on the record's bucket as well as its
// kind. An un-annotated ARs batch wants rev-AR; an un-annotated annual list
// wants rev-cases — the same 'not_reviewed' state, two different tools. This
// is why the mapping lives here rather than as a field on the state itself:
// the state machine describes what IS, this decides what to DO about it.
// Every route that would end in "run rev-cases" goes through here, so the
// three states that can reach it cannot drift apart. Two things stop it: an
// exhausted pool (nothing left to find) and an ARs batch still out with the
// agency (nothing can be ordered with whatever it does find).
function revCasesOrWait(state) {
  if (state.poolExhausted || state.lowRated) return null;
  return state.blockedBy ? 'waiting' : 'run_rev_cases';
}

export function nextActionFor(state) {
  if (!state) return null;
  const isLog = ['logs', 'uof', 'complaints'].includes(state.bucket);
  switch (state.kind) {
    case 'to_request':      return 'file_request';
    case 'unrated_report':  return 'rate_report';
    case 'unmined':         return 'pull_batch';
    case 'unexpanded':      return 'expand_rev_ar';
    // 'withheld' deliberately returns null: it stays a Records-page pill
    // (worth knowing an agency held reports back) but is not worklist work.
    case 'withheld':        return null;
    case 'unmined_held':    return 'waiting';
    case 'needs_rev_cases': return revCasesOrWait(state);
    // An annual list with pasted rows but no rev-cases verdict is still a
    // rev-cases job — hand-annotating a 1,200-row arrest log is not the
    // workflow. Its Records-page pill is left alone; only the action differs.
    // Unless the pool is already exhausted, in which case there is genuinely
    // nothing left to do and the row drops out of the worklist entirely.
    case 'not_reviewed':    return isLog ? revCasesOrWait(state) : 'run_rev_ar';
    case 'partial':         return isLog ? revCasesOrWait(state) : 'annotate';
    default:                return null; // 'done'
  }
}
